import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  IndianRupee, Receipt, Package, Wallet, Boxes, AlertTriangle, CalendarX, Truck, ScanBarcode,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { KpiCard, PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, inrShort, num, dmyTime } from "@/lib/format";

export default function PharmacyDashboard() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!can("pharmacy.dashboard")) return;
    api.get("/pharmacy/dashboard").then(({ data }) => setD(data)).catch((e) => setError(apiError(e)));
  }, [can]);

  if (!can("pharmacy.dashboard")) return <NoAccess what="the pharmacy dashboard" />;
  if (error) return <ErrorState message={error} />;
  if (!d) return <Loading label="Loading pharmacy dashboard…" />;

  return (
    <div data-testid="pharmacy-dashboard">
      <PageHeader overline="Pharmacy" title="Pharmacy dashboard"
        subtitle="Counter, stock and financial position for today. Alerts open the exact filtered records."
        actions={can("pharmacy.sales.create") && (
          <Link to="/app/pharmacy/pos" data-testid="open-pos-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white">
            <ScanBarcode className="h-4 w-4" /> Open POS
          </Link>
        )} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Today's sales" value={inrShort(d.today_sales)} hint={`${num(d.today_invoices)} invoices`} tone="accent" icon={IndianRupee} to="/app/pharmacy/sales" testid="kpi-today-sales" />
        <KpiCard label="Today's purchases" value={inrShort(d.today_purchases)} icon={Package} to="/app/pharmacy/purchases" testid="kpi-today-purchases" />
        <KpiCard label="Today's expenses" value={inrShort(d.today_expenses)} icon={Wallet} to="/app/pharmacy/expenses" testid="kpi-today-expenses" />
        <KpiCard label="Net sales" value={inrShort(d.net_sales)} hint={`Gross ${inrShort(d.gross_sales)}`} tone="success" icon={IndianRupee} to="/app/pharmacy/reports" testid="kpi-net-sales" />
        <KpiCard label="Low stock medicines" value={num(d.low_stock)} tone="warning" icon={AlertTriangle} to="/app/pharmacy/inventory?filter=low_stock" testid="kpi-low-stock" />
        <KpiCard label="Expiring medicines" value={num(d.expiring)} tone="warning" icon={CalendarX} to="/app/pharmacy/inventory?filter=expiring" testid="kpi-expiring" />
        <KpiCard label="Expired medicines" value={num(d.expired)} tone="danger" icon={CalendarX} to="/app/pharmacy/inventory?filter=expired" testid="kpi-expired" />
        <KpiCard label="Medicines" value={num(d.medicines)} hint={`${num(d.suppliers)} suppliers`} icon={Boxes} to="/app/pharmacy/medicines" testid="kpi-medicines" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="panel">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-ui font-bold tracking-tight">Recent invoices</h3>
            <Link to="/app/pharmacy/sales" className="text-xs text-slate-500 hover:text-slate-900">View all</Link>
          </div>
          {d.recent_sales.length === 0 ? <EmptyState title="No invoices yet" subtitle="Bill your first customer from the POS counter." testid="recent-sales-empty" /> : (
            <div className="divide-y divide-slate-100">
              {d.recent_sales.map((s) => (
                <div key={s.sale_id} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{s.invoice_no}</p>
                    <p className="text-xs text-slate-500 truncate">{s.customer_name} · {dmyTime(s.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold">{inr(s.total)}</p>
                    <StatusPill status={s.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="panel">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-ui font-bold tracking-tight">Recent purchases</h3>
            <Link to="/app/pharmacy/purchases" className="text-xs text-slate-500 hover:text-slate-900">View all</Link>
          </div>
          {d.recent_purchases.length === 0 ? <EmptyState title="No purchases yet" subtitle="Record a purchase to create batches and raise stock." testid="recent-purchases-empty" /> : (
            <div className="divide-y divide-slate-100">
              {d.recent_purchases.map((p) => (
                <div key={p.purchase_id} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{p.purchase_no}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1.5"><Truck className="h-3 w-3" />{p.supplier_name}</p>
                  </div>
                  <p className="text-sm font-semibold shrink-0">{inr(p.total)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
